import "./Landing.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { ButtonCard } from "@amzn/stencil-react-components/card";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import {
  ModalBody,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  WithModal,
} from "@amzn/stencil-react-components/modal";
import { Radio } from "@amzn/stencil-react-components/radio";
import {
  H3,
  Label,
  Text,
  TextVariant,
} from "@amzn/stencil-react-components/text";
import { TextArea } from "@amzn/stencil-react-components/text-area";
import IconQuestionCircleFillLarge from "@amzn/stencil-react-icons/icons/icon-question-circle-fill-large";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { EmbeddedMetric, Logger } from "@/apis/Logger";
import Header from "@/components/Header";
import {
  enterWizard,
  IDPRISM_AUTH_VALUE,
  isNonProd,
  isWindows,
  LANDING_URLS,
  openExternalLink,
} from "@/helpers/landing";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

import contractorBanner from "./assets/contractorEmployeeBanner.svg";
import fullTimeBanner from "./assets/fullTimeEmployeeBanner.svg";
import transferBanner from "./assets/transferEmployeeBanner.svg";
import welcomeBanner from "./assets/welcomeToAmazonSplashScreenBanner.svg";

const PADDING = "2rem";
const GAP = "2rem";

/**
 * The stable, locale-independent value of the "Other" help option. Used to
 * key the "provide details" branch and as the metric value, so neither depends
 * on the translated label the user sees.
 */
const OTHER_OPTION = "Other, not listed";

/**
 * Emit a landing-page Snowplow event. The optional `property` carries the
 * per-event dimension (CardType or the chosen help option).
 */
const logEvent = (action: Day1EventAction, property?: string): void =>
  trackStructEvent({
    category: Day1EventCategory.Landing,
    action,
    property,
  });

/**
 * Render the `<b>` chunks of a card description. The app-wide `b` element is a
 * variant-less Stencil Text, which resets to the body font and size; repeating
 * the description's variant keeps the bold words in the same font and size.
 */
const renderCardEmphasis = (chunks: React.ReactNode[]): JSX.Element => (
  <Text variant={TextVariant.HeadingLG} fontWeight="bold" display="inline">
    {chunks}
  </Text>
);

type CardType = "EZO" | "FirstAid" | "LaptopSetup";

const cards: ReadonlyArray<{
  banner: string;
  cardType: CardType;
  titleId: string;
  descriptionId: string;
  onClick: () => void;
}> = [
  {
    banner: fullTimeBanner,
    cardType: "EZO",
    titleId: "landing-card-corporate-title",
    descriptionId: "landing-card-corporate-description",
    // Reload into the authenticated wizard with the splash param stripped.
    onClick: enterWizard,
  },
  {
    banner: contractorBanner,
    cardType: "FirstAid",
    titleId: "landing-card-contractor-title",
    descriptionId: "landing-card-contractor-description",
    // On non-prod stages this card is repurposed to exercise the IDPrism auth
    // path: strip the splash param and set `auth=idprism` so the wizard's
    // Cognito redirect drops the PPv2 login hint. On prod it keeps the First
    // Aid destination.
    onClick: () =>
      isNonProd()
        ? enterWizard({ auth: IDPRISM_AUTH_VALUE })
        : openExternalLink(LANDING_URLS.FIRST_AID),
  },
  {
    banner: transferBanner,
    cardType: "LaptopSetup",
    titleId: "landing-card-transfer-title",
    descriptionId: "landing-card-transfer-description",
    onClick: () => openExternalLink(LANDING_URLS.LAPTOP_SETUP),
  },
];

/**
 * Help options. `value` is the stable English identity used for comparisons
 * and metrics; `messageId` is the translated label shown to the user; `id`
 * associates the radio input with its label for accessibility.
 */
const HELP_OPTIONS: ReadonlyArray<{
  id: string;
  value: string;
  messageId: string;
}> = [
  {
    id: "landing-help-option-noneApply",
    value: "None of the groups apply to me",
    messageId: "landing-help-option-noneApply",
  },
  {
    id: "landing-help-option-frozen",
    value: "Screen frozen or not responding",
    messageId: "landing-help-option-frozen",
  },
  {
    id: "landing-help-option-login",
    value: "Login or credential issues",
    messageId: "landing-help-option-login",
  },
  {
    id: "landing-help-option-other",
    value: OTHER_OPTION,
    messageId: "landing-help-option-other",
  },
];

/**
 * The unauthenticated landing page shown when the web app is loaded with
 * `?splash=1`. Ported from the Electron native splash screen: the new hire
 * picks the audience that matches them (which routes them to the wizard, First
 * Aid, or the laptop setup guide) or opens the "I need help" flow.
 */
export default function LandingPage(): JSX.Element {
  const intl = useIntl();

  const [selectedOption, setSelectedOption] = useState("");
  const [otherText, setOtherText] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    logEvent(Day1EventAction.LandingLoaded);
    Logger.publishInfoMetric(
      new EmbeddedMetric("LandingScreenLoaded", "Count", 1)
    );
  }, []);

  const getOptionValue = (option: string): string =>
    option === OTHER_OPTION && otherText.trim()
      ? `Other: ${otherText.trim()}`
      : option;

  const resetModal = (): void => {
    setSelectedOption("");
    setOtherText("");
    setSubmitted(false);
  };

  // The page navigates away immediately on click (reload for the wizard, a new
  // tab/window for the external destinations), so there is no need to track a
  // "selected" card in local state the way the native splash did.
  const handleCardClick = (cardType: CardType, onClick: () => void): void => {
    logEvent(Day1EventAction.LandingCardClicked, cardType);
    onClick();
  };

  const handleChatWithITClick = (close: () => void) => (): void => {
    logEvent(Day1EventAction.LandingChatWithITClicked);
    openExternalLink(LANDING_URLS.CHAT_WITH_IT);
    resetModal();
    close();
  };

  const handleModalCancel = (close: () => void) => (): void => {
    logEvent(Day1EventAction.LandingHelpModalCancelled);
    resetModal();
    close();
  };

  const handleConfirmationClose = (close: () => void) => (): void => {
    logEvent(Day1EventAction.LandingHelpConfirmationClosed);
    resetModal();
    close();
  };

  const handleSubmit = (): void => {
    logEvent(
      Day1EventAction.LandingHelpFormSubmitted,
      getOptionValue(selectedOption)
    );
    setSubmitted(true);
  };

  const handleOptionSelect = (option: string) => (): void => {
    setSelectedOption(option);
    logEvent(Day1EventAction.LandingHelpOptionSelected, option);
  };

  const handleHelpOpen =
    (open: () => void) =>
    (e: React.MouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      // Reset here rather than only on the close handlers: a dismissal that
      // bypasses them (e.g. Escape or an overlay click) would otherwise leave
      // stale state, so a reopened modal could show the confirmation screen
      // instead of the form.
      resetModal();
      logEvent(Day1EventAction.LandingHelpModalOpened);
      open();
    };

  return (
    <div
      data-test-id="landingRoot"
      className={`landing-root${
        isWindows() ? " landing-root--windows-scale" : ""
      }`}
    >
      <Header showGetSupport={false} />
      <Col padding={PADDING} gridGap={GAP} className="landing-container">
        <img
          src={welcomeBanner}
          alt={intl.formatMessage({ id: "landing-welcomeBanner-alt" })}
          className="landing-welcome-banner"
        />
        <Row gridGap={GAP} className="landing-cards-row">
          {cards.map(
            ({ banner, titleId, descriptionId, cardType, onClick }) => {
              const title = intl.formatMessage({ id: titleId });
              return (
                <ButtonCard
                  key={cardType}
                  flex={1}
                  padding={0}
                  onClick={() => handleCardClick(cardType, onClick)}
                  className="landing-button-card"
                >
                  <Col width="100%">
                    <img
                      src={banner}
                      alt={title}
                      className="landing-card-banner"
                    />
                    <Col
                      padding="1.5rem"
                      alignItems="flex-start"
                      gridGap="dimensions.spacing.200"
                    >
                      <H3
                        variant={TextVariant.HeadingXL}
                        textAlign="left"
                        className="landing-card-title"
                      >
                        {title}
                      </H3>
                      <Text
                        variant={TextVariant.HeadingLG}
                        fontWeight="regular"
                        textAlign="left"
                      >
                        <FormattedMessage
                          id={descriptionId}
                          values={{ b: renderCardEmphasis }}
                        />
                      </Text>
                    </Col>
                  </Col>
                </ButtonCard>
              );
            }
          )}
        </Row>
        <div className="landing-help-footer">
          <WithModal
            renderModal={({ close }) => (
              <ModalContainer maxWidth="50rem">
                {submitted ? (
                  <>
                    <ModalHeader
                      onCloseButtonClick={handleConfirmationClose(close)}
                    >
                      <H3 variant={TextVariant.HeadingXL}>
                        <FormattedMessage id="landing-help-confirmation-title" />
                      </H3>
                    </ModalHeader>
                    <ModalBody>
                      <Text
                        variant={TextVariant.HeadingLG}
                        fontWeight="regular"
                        className="landing-modal-section-top"
                      >
                        <FormattedMessage id="landing-help-confirmation-body" />
                      </Text>
                    </ModalBody>
                    <ModalFooter>
                      <Row
                        gridGap="dimensions.spacing.300"
                        justifyContent="flex-end"
                        className="landing-modal-footer-actions"
                      >
                        <Button
                          variant={ButtonVariant.Secondary}
                          onClick={handleConfirmationClose(close)}
                        >
                          <FormattedMessage id="landing-help-confirmation-close" />
                        </Button>
                        <Button
                          variant={ButtonVariant.Primary}
                          onClick={handleChatWithITClick(close)}
                        >
                          <FormattedMessage id="landing-help-confirmation-chat" />
                        </Button>
                      </Row>
                    </ModalFooter>
                  </>
                ) : (
                  <>
                    <ModalHeader onCloseButtonClick={handleModalCancel(close)}>
                      <H3 variant={TextVariant.HeadingXL}>
                        <FormattedMessage id="landing-help-modal-title" />
                      </H3>
                    </ModalHeader>
                    <ModalBody>
                      <Col
                        gridGap="dimensions.spacing.300"
                        className="landing-modal-section-top"
                      >
                        {HELP_OPTIONS.map(({ id, value, messageId }) => (
                          <Row
                            key={value}
                            alignItems="center"
                            gridGap="dimensions.spacing.200"
                            className="landing-radio-label"
                          >
                            <Radio
                              id={id}
                              name="help-option"
                              value={value}
                              checked={selectedOption === value}
                              onChange={handleOptionSelect(value)}
                            />
                            <Label htmlFor={id}>
                              <Text
                                variant={TextVariant.HeadingLG}
                                fontWeight="regular"
                              >
                                <FormattedMessage id={messageId} />
                              </Text>
                            </Label>
                          </Row>
                        ))}
                        {selectedOption === OTHER_OPTION && (
                          <Col
                            gridGap="dimensions.spacing.200"
                            className="landing-other-details"
                          >
                            <Label>
                              <FormattedMessage id="landing-help-other-label" />
                            </Label>
                            <TextArea
                              placeholder={intl.formatMessage({
                                id: "landing-help-other-placeholder",
                              })}
                              className="landing-other-textarea"
                              value={otherText}
                              maxLength={1000}
                              onChange={(
                                e: React.ChangeEvent<HTMLTextAreaElement>
                              ) => setOtherText(e.target.value)}
                            />
                            <Text
                              variant={TextVariant.BodySM}
                              textAlign="right"
                            >
                              {otherText.length}/1000
                            </Text>
                          </Col>
                        )}
                      </Col>
                    </ModalBody>
                    <ModalFooter>
                      <Row
                        gridGap="dimensions.spacing.300"
                        justifyContent="flex-end"
                        className="landing-modal-footer-actions"
                      >
                        <Button
                          variant={ButtonVariant.Secondary}
                          onClick={handleModalCancel(close)}
                        >
                          <FormattedMessage id="landing-help-cancel" />
                        </Button>
                        <Button
                          variant={ButtonVariant.Primary}
                          onClick={handleSubmit}
                          disabled={
                            !selectedOption ||
                            (selectedOption === OTHER_OPTION &&
                              !otherText.trim())
                          }
                        >
                          <FormattedMessage id="landing-help-submit" />
                        </Button>
                      </Row>
                    </ModalFooter>
                  </>
                )}
              </ModalContainer>
            )}
          >
            {({ open }) => (
              <Button
                variant={ButtonVariant.Tertiary}
                className="landing-help-button"
                onClick={handleHelpOpen(open)}
                icon={<IconQuestionCircleFillLarge aria-hidden="true" />}
              >
                <FormattedMessage id="landing-help-button" />
              </Button>
            )}
          </WithModal>
        </div>
      </Col>
    </div>
  );
}
